import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { User, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";

const ITEMS_PER_PAGE = 20;

const SearchByName = () => {
  const { name } = useParams();
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  
  // Memoize name parsing to prevent infinite loops
  const { nameParts, displayName, firstName, lastName } = useMemo(() => {
    const parts = name ? name.split('-').filter(Boolean) : [];
    const display = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    
    let first = '';
    let last = '';
    if (parts.length === 1) {
      last = parts[0];
    } else if (parts.length >= 2) {
      first = parts[0];
      last = parts.slice(1).join(' ');
    }
    
    return { nameParts: parts, displayName: display, firstName: first, lastName: last };
  }, [name]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!name) return;
      
      setLoading(true);
      try {
        const params = {
          limit: ITEMS_PER_PAGE,
          skip: (currentPage - 1) * ITEMS_PER_PAGE
        };
        
        if (firstName) params.first_name = firstName;
        if (lastName) params.last_name = lastName;
        
        const data = await api.searchProviders(params);
        setResults(data.results || []);
        setTotalCount(data.result_count || 0);
      } catch (error) {
        console.error("Error searching by name:", error);
        setResults([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [name, firstName, lastName, currentPage]);

  const pageTitle = "NPI Lookup by Name — " + displayName + " Provider Search";
  const pageDescription = "NPI lookup for " + displayName + " — " + totalCount.toLocaleString() + " providers found in the NPPES national NPI registry. Verify NPI numbers, specialty, and credentials.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={"https://npilookup.com/search/name/" + name} />
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-navy" />
              </div>
            </div>
            
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="search-name-title">
              Providers Named "{displayName}"
            </h1>
            <p className="text-slate-500 text-lg">
              Healthcare providers matching this name
            </p>
            
            <div className="mt-4">
              <span className="text-sm text-teal font-medium bg-teal/10 px-3 py-1 rounded-full" data-testid="search-name-count">
                {loading ? "Searching..." : totalCount.toLocaleString() + " providers found"}
              </span>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found with this name
              </h3>
              <p className="text-slate-500 mb-6">
                Try a different spelling or search by specialty instead.
              </p>
              <Link to="/">
                <Button variant="outline">New Search</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="search-name-results">
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (totalPages <= 5) pageNum = i + 1;
                    if (currentPage >= totalPages - 2 && totalPages > 5) pageNum = totalPages - 4 + i;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={currentPage === pageNum ? 'bg-navy' : ''}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchByName;
